'use client';

import React, {
  FunctionComponent,
  FormEvent,
  useState,
  useEffect,
  ChangeEvent,
} from "react";
import dynamic from "next/dynamic";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { SearchFilterProps } from "@/lib/types/filters";
import styles from "./searchFilter.module.scss";

const searchFieldLabel = "categorySearch";

const Input = dynamic(() => import("@/components/input"), {
  ssr: false,
  loading: () => <input type="text" id={searchFieldLabel} />,
});

const SearchFilter: FunctionComponent<SearchFilterProps> = ({
  title = "",
  query,
  setQuery,
  onSearch,
}) => {
  const [localQuery, setLocalQuery] = useState(query);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setQuery(localQuery);
    }, 700);

    return () => clearTimeout(delayDebounce);
  }, [localQuery, setQuery]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSearch?.();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalQuery(e.target.value);
  };

  const clearQuery = () => {
    setLocalQuery("");
    setQuery("");
    onSearch?.();

    // Build new search params without 'q' or 'force'
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.delete("q");
    newParams.delete("force");

    // Replace URL without causing a full reload
    router.replace(`${pathname}?${newParams.toString()}`, { scroll: false });
  };

  return (
    <form className={styles.searchContainer} onSubmit={handleSubmit}>
      <label className={styles.title} htmlFor={searchFieldLabel}>
        Search {title}
      </label>
      <div className={styles.formFields}>
        <div className={styles.inputField}>
          <Input
            id={searchFieldLabel}
            type="text"
            name="search"
            value={localQuery}
            onChange={handleChange}
          />
          {localQuery && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={clearQuery}
              aria-label="Clear search input"
            >
              ×
            </button>
          )}
        </div>
        <input
          type="submit"
          className={styles.submitButton}
          value="Search Now"
        />
      </div>
    </form>
  );
};

export default SearchFilter;
